import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight,
  Download,
  FileText,
  Loader2,
  Lock,
  RotateCcw,
} from "lucide-react";
import type { EvalState, PageView } from "@/types/maturity";
import { useToast } from "@/hooks/use-toast";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useAuth } from "@/contexts/AuthContext";
import { PaywallDialog } from "@/components/PaywallDialog";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { generateMaturityXlsxReport } from "@/utils/generate-maturity-xlsx-report";

type CTABarProps = {
  evalState: EvalState;
  sessionId: string;
  assuranceLevel: string;
  fullResponses: Record<string, number>;
  fullNotes: Record<string, string>;
  onNavigate: (view: PageView) => void;
  onResetFullEval: () => void;
  isResetting?: boolean;
};

function CTABar({
  evalState,
  sessionId,
  assuranceLevel,
  fullResponses,
  fullNotes,
  onNavigate,
  onResetFullEval,
  isResetting,
}: CTABarProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data: xlsxCreditCheck, isLoading: isCheckingCredits } =
    useCheckActionCredits(
      {
        session_id: sessionId,
        action_type: CREDIT_ACTION_TYPES.S3_RAPORT_PDF_V2,
      },
      !!sessionId && evalState === "FULL_COMPLETE"
    );

  const { mutateAsync: deductCredits } = useDeductCredits();

  const hasInsufficientCredits =
    xlsxCreditCheck &&
    !xlsxCreditCheck.has_credits &&
    !xlsxCreditCheck.already_purchased;

  const handleExportXlsx = async () => {
    if (!user) {
      setShowPaywall(true);
      return;
    }
    if (hasInsufficientCredits) {
      setShowPaywall(true);
      return;
    }

    setIsExporting(true);
    try {
      if (!xlsxCreditCheck?.already_purchased) {
        await deductCredits({
          action_type: CREDIT_ACTION_TYPES.S3_RAPORT_PDF_V2,
          session_id: sessionId,
        });
      }
      await generateMaturityXlsxReport({
        assuranceLevel,
        fullResponses,
        fullNotes,
        sessionId,
      });
      toast({ title: "XLSX descărcat" });
    } catch (error) {
      if (error instanceof InsufficientCreditsError) {
        setShowPaywall(true);
        return;
      }
      toast({
        title: "Eroare",
        description: "XLSX-ul nu a putut fi generat. Încercați din nou.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (evalState === "NOT_STARTED") {
    return (
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <h3 className="font-heading text-white font-bold text-lg">
            Pasul următor: Evaluarea preliminară
          </h3>
          <p className="text-slate-400 text-sm mt-1">
            Obține o estimare rapidă a nivelului de maturitate al organizației
            tale conform CyFun® 2025.
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <Button
            size="lg"
            className="gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 text-base px-6"
            onClick={() => onNavigate("preliminary")}
          >
            Începe evaluarea preliminară
            <ArrowRight className="w-4 h-4" />
          </Button>
          <span className="text-xs text-slate-500 mt-2">
            durată completare &lt; 15 min
          </span>
        </div>
      </div>
    );
  }

  if (evalState === "PRELIM_COMPLETE") {
    return (
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 space-y-5">
        <h3 className="font-heading text-white font-bold text-lg">
          Pasul următor: Evaluarea completă
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => onNavigate("full-eval")}
            className="p-5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 transition-colors text-left"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-white">Evaluare completă</div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 text-white">
                Gratuit
              </span>
            </div>
            <div className="text-sm text-blue-50/90">
              Completează singur evaluarea detaliată pe fiecare cerință
            </div>
          </button>
          <button
            onClick={() => onNavigate("assisted")}
            className="p-5 rounded-xl border border-slate-600 hover:border-emerald-400 transition-colors text-left bg-slate-800/50"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-white">Evaluare asistată</div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-700/40 text-emerald-300 border border-emerald-700/60">
                Plătit
              </span>
            </div>
            <div className="text-sm text-slate-400">
              Un specialist te ghidează în mai puțin de 2 ore
            </div>
          </button>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            disabled
            className="gap-2 border-slate-600 text-slate-500 hover:bg-transparent"
          >
            <Lock className="w-4 h-4" />
            <Download className="w-4 h-4" />
            Export XLS
          </Button>
          <Button
            variant="outline"
            disabled
            className="gap-2 border-slate-600 text-slate-500 hover:bg-transparent"
          >
            <Lock className="w-4 h-4" />
            <FileText className="w-4 h-4" />
            Plan Remediere
          </Button>
        </div>
      </div>
    );
  }

  // FULL_COMPLETE
  const exportDisabled = isCheckingCredits || isExporting;
  const ExportIcon = isExporting
    ? Loader2
    : hasInsufficientCredits
      ? Lock
      : Download;

  return (
    <>
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <h3 className="font-heading text-white font-bold text-lg">
          Evaluare finalizată
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button
            className="gap-2 bg-white/10 hover:bg-white/20 text-white border-0"
            onClick={handleExportXlsx}
            disabled={exportDisabled}
          >
            <ExportIcon
              className={`w-4 h-4 ${isExporting ? "animate-spin" : ""}`}
            />
            {isExporting ? "Se generează..." : "Export XLS"}
          </Button>
          <Button
            className="gap-2 bg-white/10 hover:bg-white/20 text-white border-0"
            onClick={() =>
              toast({ title: "Plan Remediere va fi disponibil în curând" })
            }
          >
            <FileText className="w-4 h-4" />
            Plan Remediere
          </Button>
          <Button
            variant="ghost"
            className="gap-2 text-slate-400 hover:text-white hover:bg-white/10"
            onClick={() => setShowResetDialog(true)}
          >
            <RotateCcw className="w-4 h-4" />
            Refă evaluarea
          </Button>
        </div>
      </div>

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refă evaluarea completă?</AlertDialogTitle>
            <AlertDialogDescription>
              Ești sigur că vrei să refaci evaluarea completă? Scorurile actuale
              vor fi șterse. Evaluarea preliminară va fi păstrată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              disabled={isResetting}
              onClick={async () => {
                await onResetFullEval();
                setShowResetDialog(false);
              }}
            >
              {isResetting ? "Se resetează..." : "Refă evaluarea"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={CREDIT_ACTION_TYPES.S3_RAPORT_PDF_V2}
      />
    </>
  );
}

export default CTABar;
